// Operating-profile model for thermal-exchange loops. A weekly profile (weekday + weekend hours)
// is converted to ANNUAL run-hours, netting out public holidays for the project's country — so an
// office typology isn't assumed to run on weekends/holidays, and a residential building isn't
// assumed to be at full load during the weekday workday. Every derived number is transparent and
// user-overridable (zero fabricated constants).

// Non-leap-year day counts (Mon–Fri vs Sat–Sun). ~261 weekdays / 104 weekend days.
const WEEKDAYS = 261;
const WEEKEND_DAYS = 104;

// Statutory public-holiday counts by country (approx, editable context). Used only to subtract
// holiday weekdays from schedules that pause on holidays. Default falls back to 11.
export const PUBLIC_HOLIDAYS_BY_COUNTRY = {
  "canada": 11, "united states": 11, "usa": 11, "united kingdom": 8, "uk": 8,
  "australia": 10, "germany": 10, "france": 11, "india": 17, "japan": 16, "china": 13,
  "united arab emirates": 14, "uae": 14, "singapore": 11, "netherlands": 9, "spain": 12,
  "italy": 12, "mexico": 8, "brazil": 11, "ireland": 10, "new zealand": 12, "south africa": 12,
};
export const DEFAULT_HOLIDAYS = 11;

// Weekly profile presets. wkd/wke = operating hours per weekday / weekend-day.
export const OPERATING_PROFILES = {
  continuous:  { label: "Continuous (24/7)",              wkd: 24, wke: 24, holidays: false, dayShare: 0.5,  note: "Runs every hour of the year." },
  office:      { label: "Office (Mon–Fri, daytime)",      wkd: 10, wke: 0,  holidays: true,  dayShare: 0.92, note: "Weekdays only; paused on weekends & public holidays." },
  retail:      { label: "Retail / commercial (7 days)",   wkd: 12, wke: 10, holidays: true,  dayShare: 0.7,  note: "Open 7 days; reduced hours on weekends; paused on major holidays." },
  school:      { label: "School / education",             wkd: 8,  wke: 0,  holidays: true,  dayShare: 0.95, note: "Weekdays only; paused on weekends & holidays (term-time approximation)." },
  residential: { label: "Residential (evenings + weekends)", wkd: 14, wke: 20, holidays: false, dayShare: 0.35, note: "Occupied evenings/nights on weekdays and most of the weekend, not the weekday workday." },
  custom:      { label: "Custom weekly hours",            wkd: 8,  wke: 0,  holidays: true,  dayShare: 0.6,  note: "Set your own weekday & weekend hours." },
};

// Fraction of a building's operating hours that fall in the daytime window (06:00–18:00). Used to
// place recovered waste heat into day vs night buckets for the temporal Energy-loop match.
export function operatingDayShare(typologyId) {
  const p = OPERATING_PROFILES[defaultProfileFor(typologyId)] || OPERATING_PROFILES.office;
  return p.dayShare;
}

// Suggest a default profile id from the building typology.
export function defaultProfileFor(typologyId) {
  const t = String(typologyId || "").toLowerCase();
  if (/resid|apartment|multifamily|housing|dwelling|hotel/.test(t)) return "residential";
  if (/retail|commercial|mixed|mall|store/.test(t)) return "retail";
  if (/school|educat|univers|campus/.test(t)) return "school";
  if (/hospital|data|industrial|lab/.test(t)) return "continuous";
  return "office";
}

// Parse a country name from a geocode label ("123 Rd, City, Country" → "Country").
export function countryFromGeo(geo) {
  const name = geo?.name || "";
  const parts = name.split(",").map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

// Annual operating hours from a weekly profile, netting holidays for the country.
// `custom` = { wkd, wke } used only when profileId === "custom".
export function annualOperatingHours(profileId, country, custom) {
  const p = OPERATING_PROFILES[profileId] || OPERATING_PROFILES.office;
  const wkd = profileId === "custom" ? Math.max(0, Math.min(24, Number(custom?.wkd) || 0)) : p.wkd;
  const wke = profileId === "custom" ? Math.max(0, Math.min(24, Number(custom?.wke) || 0)) : p.wke;
  const holidays = p.holidays ? (PUBLIC_HOLIDAYS_BY_COUNTRY[String(country || "").toLowerCase()] ?? DEFAULT_HOLIDAYS) : 0;
  const effWeekdays = Math.max(0, WEEKDAYS - holidays);
  const hours = Math.round(effWeekdays * wkd + WEEKEND_DAYS * wke);
  return Math.max(0, Math.min(8760, hours));
}

// One-line human basis for the derived hours (shown under the field).
export function profileBasis(profileId, country, custom) {
  const p = OPERATING_PROFILES[profileId] || OPERATING_PROFILES.office;
  const wkd = profileId === "custom" ? (Number(custom?.wkd) || 0) : p.wkd;
  const wke = profileId === "custom" ? (Number(custom?.wke) || 0) : p.wke;
  const holidays = p.holidays ? (PUBLIC_HOLIDAYS_BY_COUNTRY[String(country || "").toLowerCase()] ?? DEFAULT_HOLIDAYS) : 0;
  const wkeTxt = wke > 0 ? ` + ${wke} h/weekend-day` : ", no weekend";
  const holTxt = p.holidays ? ` − ${holidays} holidays (${country || "default"})` : "";
  return `${wkd} h/weekday${wkeTxt}${holTxt}`;
}
